import { CookingStep, CookingStepCondition, CookingStepConditionType } from './recipeTypes';
import { Locale } from './commonTypes';
import { productsService } from './productsService';

export type ProductConditionFieldDef = {
    fieldName: string;
    labelKey: string;
    valueType: 'boolean' | 'string';
};

export const PRODUCT_CONDITION_FIELDS: ProductConditionFieldDef[] = [
    {
        fieldName: 'before_cooking_rinse_need',
        labelKey: 'app.page.cookingStep.productField.before_cooking_rinse_need',
        valueType: 'boolean',
    },
];

export const PRODUCT_FIELD_OTHER = '__other__';

export const INGREDIENT_GROUP_OPTIONS = ['base', 'adds', 'tasty', 'liquid'] as const;

export const KASHA_CONSISTENCY_OPTIONS = ['fluffy', 'creamy', 'sticky'] as const;

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const parseGroupChips = (value?: string): string[] => (
    (value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
);

export const serializeGroupChips = (groups: string[]): string => (
    groups.filter(Boolean).join(',')
);

export const isKnownProductField = (fieldName?: string): boolean => (
    PRODUCT_CONDITION_FIELDS.some((field) => field.fieldName === fieldName)
);

export const getProductFieldDef = (fieldName?: string): ProductConditionFieldDef | undefined => (
    PRODUCT_CONDITION_FIELDS.find((field) => field.fieldName === fieldName)
);

export const formatConditionPreview = (
    condition: CookingStepCondition,
    t: Translate,
    locale: string = Locale.LOCALE_RU,
): string => {
    const type = condition.conditionType;

    if (type === CookingStepConditionType.PRODUCT_FIELD
        || type === 'product_field') {
        const group = condition.ingredientGroup
            ? t(`app.page.cookingStep.ingredientGroup.${condition.ingredientGroup}`)
            : '—';
        const fieldDef = getProductFieldDef(condition.fieldName);
        const property = fieldDef
            ? t(fieldDef.labelKey)
            : (condition.fieldName || '—');
        let valueLabel = condition.value || '—';
        if (fieldDef?.valueType === 'boolean' || condition.value === 'true' || condition.value === 'false') {
            valueLabel = condition.value === 'true'
                ? t('app.page.cookingStep.bool.yes')
                : condition.value === 'false'
                    ? t('app.page.cookingStep.bool.no')
                    : valueLabel;
        }
        return t('app.page.cookingStep.preview.productField', {
            group,
            property,
            value: valueLabel,
        });
    }

    if (type === CookingStepConditionType.PRODUCT_CODE
        || type === 'product_code') {
        const code = condition.productCode || '';
        let productLabel = code || '—';
        if (code) {
            const localized = productsService.getLocalization(locale, code, true);
            if (localized && localized !== code) {
                productLabel = `${localized} (${code})`;
            }
        }
        return t('app.page.cookingStep.preview.productCode', {
            product: productLabel,
        });
    }

    if (type === CookingStepConditionType.ANY_INGREDIENT_GROUP_PRESENT
        || type === 'any_ingredient_group_present') {
        const groups = parseGroupChips(condition.value).map((group) => (
            t(`app.page.cookingStep.ingredientGroup.${group}`)
        ));
        return t('app.page.cookingStep.preview.groupsPresent', {
            groups: groups.length ? groups.join(', ') : '—',
        });
    }

    if (type === CookingStepConditionType.KASHA_CONSISTENCY
        || type === 'kasha_consistency') {
        const code = condition.value || '';
        const label = code
            ? t(`recipe.scheme.kashaConsistency.${code}`)
            : '—';
        return t('app.page.cookingStep.preview.kashaConsistency', { value: label });
    }

    if (type === CookingStepConditionType.KASHA_CONSISTENCY_IN
        || type === 'kasha_consistency_in') {
        const labels = parseGroupChips(condition.value).map((code) => (
            t(`recipe.scheme.kashaConsistency.${code}`)
        ));
        return t('app.page.cookingStep.preview.kashaConsistencyIn', {
            values: labels.length ? labels.join(', ') : '—',
        });
    }

    return t('app.page.cookingStep.preview.unknown');
};

export type ConditionErrors = {
    ingredientGroup?: boolean;
    fieldName?: boolean;
    value?: boolean;
    productCode?: boolean;
};

export const validateCondition = (condition: CookingStepCondition): ConditionErrors => {
    const errors: ConditionErrors = {};
    const type = condition.conditionType;

    if (type === CookingStepConditionType.PRODUCT_FIELD || type === 'product_field') {
        if (!condition.ingredientGroup) {
            errors.ingredientGroup = true;
        }
        if (!condition.fieldName) {
            errors.fieldName = true;
        }
        if (!condition.value) {
            errors.value = true;
        }
    }

    if (type === CookingStepConditionType.PRODUCT_CODE || type === 'product_code') {
        if (!condition.productCode) {
            errors.productCode = true;
        }
    }

    if (type === CookingStepConditionType.ANY_INGREDIENT_GROUP_PRESENT
        || type === 'any_ingredient_group_present') {
        if (!parseGroupChips(condition.value).length) {
            errors.value = true;
        }
    }

    if (type === CookingStepConditionType.KASHA_CONSISTENCY
        || type === 'kasha_consistency'
        || type === CookingStepConditionType.KASHA_CONSISTENCY_IN
        || type === 'kasha_consistency_in') {
        if (!parseGroupChips(condition.value).length) {
            errors.value = true;
        }
    }

    return errors;
};

export const conditionHasErrors = (condition: CookingStepCondition): boolean => (
    Object.keys(validateCondition(condition)).length > 0
);

export type StepErrors = {
    stepCode?: boolean;
    textRu?: boolean;
    prosePlaceholders?: boolean;
    conditions?: boolean;
};

export const validateStep = (step: CookingStep): StepErrors => {
    const errors: StepErrors = {};
    if (!(step.stepCode || '').trim()) {
        errors.stepCode = true;
    }
    const measureBlock = (step.measureBlock || 'none').toLowerCase();
    const hasMeasure = measureBlock !== 'none';
    const ru = (step.texts || []).find((item) => item.locale === Locale.LOCALE_RU);
    if (!hasMeasure && !(ru?.text || '').trim()) {
        errors.textRu = true;
    }
    if ((step.texts || []).some((item) => (item.text || '').includes('{{'))) {
        errors.prosePlaceholders = true;
    }
    if ((step.conditions || []).some(conditionHasErrors)) {
        errors.conditions = true;
    }
    return errors;
};

export const stepHasErrors = (step: CookingStep): boolean => (
    Object.keys(validateStep(step)).length > 0
);

export const formatStepConditionsSummary = (
    step: CookingStep,
    t: Translate,
    locale: string = Locale.LOCALE_RU,
): string => {
    const conditions = step.conditions || [];
    if (!conditions.length) {
        return t('app.page.cookingStep.preview.always');
    }
    return conditions
        .map((condition) => formatConditionPreview(condition, t, locale))
        .join(` ${t('app.page.cookingStep.preview.and')} `);
};
